import type { CreateAdminProfessionDto, UpdateAdminProfessionDto } from '@idc-forms/api-client'
import type {
  V2CreateAdminProfessionInput,
  V2UpdateAdminProfessionInput
} from '@idc-forms/schemas/api-v2/admin-professions'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import { professionsAdminV2Service } from '../services/professions-admin-v2.service'

/**
 * Mutaciones del catálogo de profesiones contra la API v2. Identifican por id
 * público (`prf_`) y, al completar, invalidan todo el árbol `['professions']`
 * (listado + summary + lookup + detalle). El alta/edición vive en el side panel.
 */
function useV2AccessToken() {
  const { auth } = useAuth()
  return auth?.v2AccessToken ?? ''
}

export const useCreateProfessionV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    // Se acepta el tipo de DOMINIO (schema Zod) y se castea en el borde del SDK:
    // el generado y el schema describen el mismo dato en runtime pero TS los ve
    // como tipos distintos.
    mutationFn: async (body: V2CreateAdminProfessionInput) =>
      await professionsAdminV2Service.create(accessToken, body as CreateAdminProfessionDto),
    onSuccess: () => {
      toast.success('Profesión creada')
      void queryClient.invalidateQueries({ queryKey: queryKeys.professions.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useUpdateProfessionV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: V2UpdateAdminProfessionInput }) =>
      await professionsAdminV2Service.update(accessToken, id, body as UpdateAdminProfessionDto),
    onSuccess: () => {
      toast.success('Profesión actualizada')
      void queryClient.invalidateQueries({ queryKey: queryKeys.professions.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useDeleteProfessionV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async (id: string) => await professionsAdminV2Service.remove(accessToken, id),
    onSuccess: () => {
      toast.success('Profesión eliminada')
      void queryClient.invalidateQueries({ queryKey: queryKeys.professions.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}
